/**
 * Pure mappers between the two scorecard shapes the step straddles:
 *   - Wizard payload  (`payloadRef.current.scorecard` + API hydrate shape)
 *   - ScorecardTemplateBuilder form values (what the builder renders)
 *
 * Kept side-effect-free so tests can exercise the round-trips without
 * React / kula-ai dependencies — the component only composes these.
 */
import type {
  ScorecardItem,
  ScorecardTemplateFormValues,
} from '@kula-ai/components';

interface ApiTemplate {
  name: string;
  questions?: Array<{
    type?: string;
    question?: string;
    description?: string;
    options?: string[];
  }>;
  feedback_description?: string;
  is_feedback_mandatory?: boolean;
  rating_description?: string;
}

/** Map an ATS scorecard template row into the builder's form values. */
export function templateToFormValues(
  template: ApiTemplate | undefined
): ScorecardTemplateFormValues {
  if (!template) {
    return {
      scoreCards: [],
      feedback_description: '',
      is_feedback_mandatory: false,
      rating_description: '',
    };
  }

  const scoreCards: ScorecardItem[] = (template.questions ?? []).map(
    (q, i) => ({
      id: String(i),
      question: q.question ?? '',
      description: q.description,
      type: (q.type as ScorecardItem['type']) ?? 'open_question',
      options: q.options?.map((o) => ({ optionText: o })),
    })
  );

  return {
    scoreCards,
    feedback_description: template.feedback_description ?? '',
    is_feedback_mandatory: Boolean(template.is_feedback_mandatory),
    rating_description: template.rating_description ?? '',
  };
}

/**
 * Map a hydrated `payloadRef.current.scorecard` object (from
 * /ats_interviews/:id, /ats_interviews/new, or a plan child) into
 * the builder's form values. Accepts both camelCase
 * (`feedbackDescription`) and snake_case (`feedback_description`)
 * keys so the scheduling wizard + plan editor share this mapper.
 */
export function payloadToFormValues(
  sc: Record<string, unknown>
): ScorecardTemplateFormValues {
  const questions = Array.isArray(sc.questions) ? sc.questions : [];

  // Server round-trips can ship duplicate question ids (e.g. when a plan
  // child is cloned without re-keying) — RHF's `useFieldArray` keys on
  // form-level `id` and gets confused, manifesting as ghost rows in the
  // UI. Collapse to the first occurrence per id. The id fallback below
  // (`q.id ?? i`) keeps null/undefined ids unique via their index.
  const seenIds = new Set<string>();
  const dedupedQuestions: Array<{ q: Record<string, unknown>; i: number }> = [];
  for (let i = 0; i < questions.length; i++) {
    const q = questions[i] as Record<string, unknown>;
    const id = String(q.id ?? i);
    if (seenIds.has(id)) continue;
    seenIds.add(id);
    dedupedQuestions.push({ q, i });
  }

  return {
    scoreCards: dedupedQuestions.map(({ q, i }) => {
      const rawOptions = Array.isArray(q.options)
        ? (q.options as unknown[])
        : null;
      // Same story for option text: dedupe by trimmed/cased value so a
      // payload with two identical options doesn't render two rows that
      // schema validation would later flag — we'd rather not render the
      // duplicate at all than show a confusing "this row is a duplicate
      // of this row" state on a fresh prefill.
      let options: ScorecardItem['options'];
      if (rawOptions) {
        const seenOpt = new Set<string>();
        options = [];
        for (const o of rawOptions) {
          const optionText =
            typeof o === 'string'
              ? o
              : ((o as { optionText?: string })?.optionText ?? String(o));
          const key = optionText.trim().toLowerCase();
          // Allow multiple empty rows through — the schema requires the
          // user to fill them; pre-collapsing would hide that work.
          if (key && seenOpt.has(key)) continue;
          if (key) seenOpt.add(key);
          options.push({ optionText });
        }
      }
      return {
        id: String(q.id ?? i),
        question: (q.question as string) ?? '',
        description: q.description as string | undefined,
        type: (q.type as ScorecardItem['type']) ?? 'open_question',
        mandatory: Boolean(q.mandatory),
        options,
      };
    }),
    feedback_description:
      (sc.feedbackDescription as string) ??
      (sc.feedback_description as string) ??
      '',
    is_feedback_mandatory: Boolean(sc.is_feedback_mandatory),
    rating_description:
      (sc.ratingDescription as string) ??
      (sc.rating_description as string) ??
      '',
  };
}
